import { AdminTransaction } from '../../../api/transaction'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

interface InvoiceModalProps {
  transaction: AdminTransaction | null
}

function InvoiceModal({ transaction }: InvoiceModalProps) {
  // Helper untuk format tanggal
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    })
  }

  const formatRupiah = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) return 'Rp 0'

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numAmount)
  }

  const toReadable = (value?: string | null) => {
    if (!value) return '-'
    const raw = value.toString().trim()
    const normalized = raw
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

    const words = normalized.split(' ')
    const capitalized = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1),
    )
    return capitalized.join(' ')
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-success'
      case 'pending':
        return 'bg-warning'
      case 'failed':
        return 'bg-danger'
      case 'cancelled':
        return 'bg-secondary'
      default:
        return 'bg-info'
    }
  }

  const generateInvoicePDF = async () => {
    if (!transaction) return

    try {
      // Get the invoice modal content
      const invoiceElement = document.getElementById('invoice-content')
      if (!invoiceElement) return

      // Create canvas from HTML with zoom out
      const canvas = await html2canvas(invoiceElement, {
        scale: 1.5, // Reduced scale for zoom out effect
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: invoiceElement.scrollWidth,
        height: invoiceElement.scrollHeight,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4') // 'p' for portrait orientation

      // Calculate dimensions for portrait A4
      const pageWidth = 210 // A4 portrait width in mm
      const pageHeight = 297 // A4 portrait height in mm
      const margin = 10 // margin in mm
      const contentWidth = pageWidth - margin * 2 // 190mm

      // Calculate image dimensions to fit content width
      const imgWidth = contentWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      let heightLeft = imgHeight
      let position = margin

      // Add first page
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
      heightLeft -= pageHeight - margin * 2

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + margin
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
        heightLeft -= pageHeight - margin * 2
      }

      // Download the PDF
      const fileName = `Invoice-${transaction.order_id}.pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Terjadi kesalahan saat membuat PDF. Silakan coba lagi.')
    }
  }
  return (
    <div className="modal fade" id="view_invoice">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5>Invoice</h5>
            <div className="d-flex align-items-center gap-2">
              {transaction && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={generateInvoicePDF}
                >
                  <i className="fa-solid fa-download me-1" />
                  Download PDF
                </button>
              )}
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
          </div>
          <div className="modal-body">
            {!transaction ? (
              <div className="text-center py-5">Loading...</div>
            ) : (
              <div id="invoice-content">
                <div className="border-bottom mb-3">
                  <div className="row justify-content-between align-items-center flex-wrap row-gap-4">
                    <div className="col-md-6">
                      <div className="mb-2 invoice-logo-white">
                        <img
                          src="/assets/img/logo-color.webp"
                          className="img-fluid"
                          alt="logo"
                          style={{
                            maxHeight: '50px',
                          }}
                        />
                      </div>
                      <p className="mb-2">
                        PT. Jadi Praktisi Indonesia, Bandar Lampung - Indonesia
                      </p>
                    </div>
                    <div className="col-md-6">
                      <div className=" text-end mb-3">
                        <h6 className="text-default mb-1 text-secondary fs-16">
                          #{transaction.order_id}
                        </h6>
                        <p className="mb-1">
                          Created Date :{' '}
                          <span className="text-gray-9">
                            {formatDate(transaction.created_at)}
                          </span>{' '}
                        </p>
                        <p>
                          Updated Date :{' '}
                          <span className="text-gray-9">
                            {formatDate(transaction.updated_at)}
                          </span>{' '}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-bottom mb-3">
                  <div className="row g-4">
                    <div className="col-lg-5">
                      <span className="mb-3 d-flex">From</span>
                      <div>
                        <h6 className="mb-2 fs-16">
                          PT. Jadi Praktisi Indonesia
                        </h6>
                        <p className="fs-14 mb-1">Bandar Lampung - Indonesia</p>
                        <p className="fs-14 mb-1">
                          Email : jadipraktisicom@gmail.com
                        </p>
                        <p className="fs-14">Phone : +62 851-5689-7824</p>
                      </div>
                    </div>
                    <div className="col-lg-5">
                      <span className="mb-3 d-flex">To</span>
                      <div>
                        <h6 className="mb-2">{transaction.user ? transaction.user.fullname : transaction.fullname}</h6>
                        <p className="fs-14 mb-1">
                          Email : {transaction.email}
                        </p>
                        <p className="fs-14 mb-1">
                          Transaction Type : {toReadable(transaction.type)}
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2">
                      <div className="mb-3 text-end">
                        <span className="mb-1 d-block">Payment Status</span>
                        <span
                          className={`badge ${getStatusBadgeClass(
                            transaction.status,
                          )} badge-sm d-inline-flex align-items-center fs-10 fw-normal mb-4`}
                        >
                          <i className="fa-solid fa-circle fs-5 me-1" />
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="table-responsive custom-table rounded-0">
                    <table className="table">
                      <thead className="thead-light">
                        <tr>
                          <th className="w-50">Description</th>
                          <th className="text-center">Qty</th>
                          <th className="text-end">Cost</th>
                          {/* <th className="text-end">Discount</th> */}
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="w-50">
                            <p className="text-gray-9 text-wrap">
                              {transaction.course?.title || 'Course'}
                            </p>
                          </td>
                          <td className="text-gray text-center">1</td>
                          <td className="text-gray text-end">
                            {formatRupiah(transaction.total)}
                          </td>
                          {/* <td className="text-gray text-end">Rp0</td> */}
                          <td className="text-gray text-end">
                            {formatRupiah(transaction.total)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="border-bottom mb-3 pb-3">
                  <div className="row">
                    <div className="col-md-6" />
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between align-items-center border-bottom my-2 pb-2 pe-3">
                        <p className="text-gray mb-0">Sub Total</p>
                        <p className="text-gray-9 fw-medium">
                          {formatRupiah(transaction.total)}
                        </p>
                      </div>
                      {/* <div className="d-flex justify-content-between align-items-center border-bottom my-2 pb-2 pe-3">
                        <p className="mb-0">Discount (0%)</p>
                        <p className="text-gray-9 fs-14 fw-medium">Rp0</p>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3 pe-3">
                        <p className="mb-0">VAT (0%)</p>
                        <p className="text-gray-9 fs-14 fw-medium mb-2">$0</p>
                      </div> */}
                      <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                        <h6 className="fs-16">Total Amount</h6>
                        <h6 className="fs-16">
                          {formatRupiah(transaction.total)}
                        </h6>
                      </div>
                      {/* <p>Amount in Words : Dollar One Hundred Twenty</p> */}
                    </div>
                  </div>
                </div>
                <div className="row align-items-center gy-3">
                  <div className="col-lg-8">
                    <div>
                      <h6 className="mb-1 fs-16">Terms and Conditions</h6>
                      <p>
                        Catatan: Kami tidak mengeluarkan kwitansi/invoice secara
                        cetak, silahkan print dokumen ini sebagai bukti yang sah
                        untuk keperluan kawan praktisi. Konfirmasi Pembayaran:
                        +62 852-1348-9432 (Whatsapp)
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-end">
                      <h6 className="fs-15 pe-3 mb-2">
                        PT. Jadi Praktisi Indonesia
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceModal
